import { Fragment, type ReactNode } from 'react';
import type { LoanListItem } from '@app/shared';
import { fmtMoney, type Column } from './columns';
import { DataCells, SortableHeaders } from './TableParts';
import { formatDate as fmt, addDays, addMonths, isPastDate as isPast } from '../dates';

// ---------------------------------------------------------------------------
// The loans grid, shared by the analyst/admin and Special Assets views.
//
// Both views now differ only in which columns they show and what the signed-in
// user may do — so they share one component rather than two that have to be
// kept in step. The expand row in particular exists once: whatever an analyst
// can see by expanding a row, an SA rep sees too.
// ---------------------------------------------------------------------------

type Props = {
  loans: LoanListItem[];
  columns: Column[];
  onSort: (id: string) => void;
  indicator: (id: string) => string;
  expanded: Set<number>;
  onToggleExpand: (id: number) => void;
  onOpenDetail: (id: number) => void;
  canNewPkg: boolean;
  canRestore: boolean;
  actionLoading: number | null;
  onNewPackage: (l: LoanListItem) => void;
  onRestore: (id: number) => void;
  /** Rendered as an attached footer strip below the scroll box. */
  pager?: ReactNode;
};

export function LoansTable({
  loans, columns, onSort, indicator, expanded, onToggleExpand, onOpenDetail,
  canNewPkg, canRestore, actionLoading, onNewPackage, onRestore, pager,
}: Props) {
  return (
    // The shell owns the border and clips it, so the scroll box's square
    // corners can't poke out past the rounding, and the pager reads as part
    // of the same component rather than a detached bar.
    <div className="grid-shell">
      <div className="table-wrapper">
        <table className="loans-table">
          <thead>
            <tr>
              <th />
              <SortableHeaders columns={columns} onSort={onSort} indicator={indicator} />
              <th>Flags</th>
              <th className="actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loans.map(loan => (
              <Fragment key={loan.id}>
                <tr
                  className={`loan-row${loan.isDeleted ? ' deleted' : ''}${expanded.has(loan.id) ? ' expanded' : ''}`}
                  onClick={() => onToggleExpand(loan.id)}
                >
                  <td className="expand-cell">{expanded.has(loan.id) ? '▼' : '▶'}</td>
                  <DataCells columns={columns} row={loan} />
                  <td className="flags" onClick={e => e.stopPropagation()}>
                    {loan.earlyDefault && <span className="flag flag-warn" title="Early Default">ED</span>}
                    {loan.activeBkOrLitigation && <span className="flag flag-info" title="Active BK or Litigation">BK</span>}
                  </td>
                  {/* Pinned to the right edge: the row's actions stay clickable
                      no matter how far the columns are scrolled. */}
                  <td className="actions actions-col" onClick={e => e.stopPropagation()}>
                    {canNewPkg && !loan.isDeleted && loan.phase !== 'Closed' && !loan.hasOpenPackage && (
                      <button className="btn-sm btn-primary" disabled={actionLoading === loan.id}
                        onClick={() => onNewPackage(loan)}>New Package</button>
                    )}
                    {canNewPkg && !loan.isDeleted && loan.hasOpenPackage && <span className="muted">Package open</span>}
                    {canRestore && loan.isDeleted && (
                      <button className="btn-sm" disabled={actionLoading === loan.id}
                        onClick={() => onRestore(loan.id)}>Restore</button>
                    )}
                  </td>
                </tr>
                {expanded.has(loan.id) && (
                  // Spans every column *except* the pinned Actions one, which
                  // the expand row supplies itself — so the panel stops at that
                  // boundary instead of sliding underneath it, and the pinned
                  // column's left border runs unbroken down the whole table.
                  <ExpandRow loan={loan} colSpan={columns.length + 2} onOpenDetail={onOpenDetail} />
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {pager}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ExpandRow({ loan, colSpan, onOpenDetail }: {
  loan: LoanListItem;
  colSpan: number;
  onOpenDetail: (id: number) => void;
}) {
  return (
    <tr className="expand-row">
      <td colSpan={colSpan}>
        <div className="expand-content">
          <div className="expand-grid">
            <div className="expand-item">
              <span className="k">Phase</span>
              <span className="v">{loan.phase}</span>
            </div>
            {loan.lineStatus && (
              <div className="expand-item">
                <span className="k">Line Status</span>
                <span className="v">{loan.lineStatus}</span>
              </div>
            )}
            {loan.purchaseDate && (
              <div className="expand-item">
                <span className="k">Purchase Date</span>
                <span className="v">{fmt(loan.purchaseDate)}</span>
              </div>
            )}
            {/* The two SBA clocks that start at purchase. Only meaningful once
                the loan has actually been purchased. */}
            {loan.purchaseDate && loan.phase !== 'Prepurchase' && (
              <>
                <div className="expand-item">
                  <span className="k">UPP Due (+45d)</span>
                  <span className={`v ${isPast(addDays(loan.purchaseDate, 45)) ? 'due-overdue' : ''}`}>
                    {fmt(addDays(loan.purchaseDate, 45))}
                  </span>
                </div>
                <div className="expand-item">
                  <span className="k">Extension Due (+23mo)</span>
                  <span className={`v ${isPast(addMonths(loan.purchaseDate, 23)) ? 'due-overdue' : ''}`}>
                    {fmt(addMonths(loan.purchaseDate, 23))}
                  </span>
                </div>
              </>
            )}
            <div className="expand-item">
              <span className="k">Legal Balance</span>
              <span className="v">{fmtMoney(loan.legalBalance)}</span>
            </div>
            {loan.sbaLoanStatus && (
              <div className="expand-item">
                <span className="k">Loan Status</span>
                <span className="v">{loan.sbaLoanStatus}</span>
              </div>
            )}
            {loan.etranLoanStatus && (
              <div className="expand-item">
                <span className="k">eTran Status</span>
                <span className="v">{loan.etranLoanStatus}</span>
              </div>
            )}
            <div className="expand-item">
              <span className="k">Next Action</span>
              <span className={`v ${isPast(loan.nextActionDate) ? 'due-overdue' : ''}`}>
                {fmt(loan.nextActionDate)}
              </span>
            </div>
            <div className="expand-item">
              <span className="k">Analyst</span>
              <span className="v">{loan.assignedLoanAnalystName ?? '—'}</span>
            </div>
            <div className="expand-item">
              <span className="k">Special Assets Rep</span>
              <span className="v">{loan.assignedSaOfficerName ?? '—'}</span>
            </div>
          </div>
          <div className="expand-actions">
            <span className="expand-meta">Created {fmt(loan.createdAt)} · Updated {fmt(loan.updatedAt)}</span>
            <button className="btn-sm btn-primary" onClick={() => onOpenDetail(loan.id)}>
              Open full detail →
            </button>
          </div>
        </div>
      </td>
      {/* Keeps the pinned column continuous through the expanded row. */}
      <td className="actions-col" />
    </tr>
  );
}
